// src/data/rentersTenantsDashboardData.js

/**
 * Get current language from localStorage
 */
function getCurrentLanguage() {
  console.log(localStorage);
  return localStorage.getItem("lang") || "en";
}

/**
 * Format month display based on language
 */
function formatMonthDisplay(date) {
  const lang = getCurrentLanguage();
  return date.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short",
  });
}

/**
 * Get seasonal factor for energy consumption
 */
function getSeasonalFactor(month) {
  const winterMonths = [0, 1, 11]; // Jan, Feb, Dec
  const summerMonths = [5, 6, 7]; // Jun, Jul, Aug

  if (winterMonths.includes(month)) {
    return 1.15; // +15% in winter
  } else if (summerMonths.includes(month)) {
    return 1.08; // +8% in summer (AC)
  }
  return 1.0;
}

// Get language once at module load
const lang = getCurrentLanguage();
const isGerman = lang === "de";

export const rentersTenantsDashboardData = {
  // Building/Tenant Metadata
  tenant: {
    id: 1,
    companyName: "TechStart GmbH",
    building: "TechHub München",
    location: "Munich, Germany",
    floorNumber: 3,
    leasedArea: 850, // sqm
    workstations: 45,
    meetingRooms: 4,
    conferenceRooms: 2,
    leaseStart: "2023-06-01",
    leaseEnd: "2026-05-31",
    monthlyRent: 18700, // €
    energyCostPerKwh: 0.28, // €
    co2FactorPerKwh: 0.3, // kg CO₂/kWh
    operatingHours: {
      start: "08:00",
      end: "19:00",
      weekdays: isGerman
        ? ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"]
        : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    },
    benchmarks: {
      utilizationRate: 75, // % target
      energyPerBooking: 12.5, // kWh target
      comfortIndex: 80, // % target
      avgBookingDuration: 2.5, // hours target
      co2Awareness: 70, // % target
    },
  },

  // Historical Monthly Data (Jan 2024 - Oct 2025)
  monthlyData: [
    // === JANUARY 2024 ===
    {
      month: "2024-01",
      displayMonth: formatMonthDisplay(new Date(2024, 0, 31)),
      date: "2024-01-31",

      // KPI 1: Booking Utilization Rate
      bookingUtilization: {
        rate: 68,
        changePercent: null,
        bookedSlots: 544,
        availableSlots: 800,
        peakDays: isGerman
          ? ["Dienstag", "Donnerstag"]
          : ["Tuesday", "Thursday"],
        topSpaces: [
          {
            name: isGerman ? "Innovationslabor →" : "Innovation Lab →",
            utilization: 82,
          },
          {
            name: isGerman
              ? "Vorstandssitzungssaal →"
              : "Executive Boardroom →",
            utilization: 75,
          },
        ],
        hourlyUtilization: [
          { hour: "08:00", rate: 22 },
          { hour: "09:00", rate: 45 },
          { hour: "10:00", rate: 68 },
          { hour: "11:00", rate: 82 },
          { hour: "12:00", rate: 65 },
          { hour: "13:00", rate: 58 },
          { hour: "14:00", rate: 75 },
          { hour: "15:00", rate: 88 },
          { hour: "16:00", rate: 72 },
          { hour: "17:00", rate: 55 },
          { hour: "18:00", rate: 35 },
          { hour: "19:00", rate: 18 },
        ],
      },

      // KPI 2: Energy Usage per Booking
      energyPerBooking: {
        avgEnergy: 14.2,
        changePercent: null,
        totalEnergy: 7724.8,
        totalCost: 2162.94,
        peakHours: ["08:00-11:00", "14:00-17:00"],
        topConsumers: isGerman
          ? ["Heizung", "Beleuchtung"]
          : ["Heating", "Lighting"],
        spaceBreakdown: {
          meetingRooms: 42,
          conferenceRooms: 28,
          openOffice: 20,
          breakoutAreas: 10,
        },
        floorEnergyDistribution: [
          {
            floor: isGerman ? "Besprechungsräume" : "Meeting Rooms",
            percentage: 39.11,
            change: 2.98,
            emissions: 3020,
            color: "#2D5BFF",
          },
          {
            floor: isGerman ? "Konferenzräume" : "Conference Rooms",
            percentage: 28.02,
            change: -3.25,
            emissions: 2163,
            color: "#388BFF",
          },
          {
            floor: isGerman ? "Großraumbüro" : "Open Office",
            percentage: 23.13,
            change: 0.14,
            emissions: 1787,
            color: "#93AAFD",
          },
          {
            floor: isGerman ? "Pausenbereiche" : "Breakout Areas",
            percentage: 9.74,
            change: -1.11,
            emissions: 752,
            color: "#C6D2FD",
          },
        ],
        benchmarkComparison: 88,
      },

      // KPI 3: Comfort Satisfaction Index
      comfortSatisfaction: {
        overallScore: 78,
        changePercent: null,
        energyAnalytics: 68,
        dynamicOccupancy: 72,
        utilizationAnalytics: 75,
        monthlyTrend: {
          energyUse: 1240,
          co2Emissions: 372,
        },
      },

      // KPI 4: Average Booking Duration
      bookingDuration: {
        avgDuration: 2.3,
        changePercent: null,
        totalBookings: 248,
        efficiency: 82,
        peakBookingDays: isGerman
          ? ["Dienstag", "Mittwoch", "Donnerstag"]
          : ["Tuesday", "Wednesday", "Thursday"],
        peakBookingHours: ["09:00-11:00", "14:00-16:00"],
        durationDistribution: {
          lessThan1Hr: 45,
          oneToTwoHrs: 89,
          twoToFourHrs: 67,
          fourToSixHrs: 32,
          moreThanSixHrs: 15,
        },
        roomEfficiency: [
          {
            room: isGerman
              ? "Vorstandssitzungssaal →"
              : "Executive Boardroom →",
            avgDuration: 3.2,
            efficiency: 85,
            bookings: 28,
          },
          {
            room: isGerman ? "Innovationslabor →" : "Innovation Lab →",
            avgDuration: 1.8,
            efficiency: 72,
            bookings: 45,
          },
          {
            room: isGerman
              ? "Kundenbesprechungssuite →"
              : "Client Meeting Suite →",
            avgDuration: 4.5,
            efficiency: 95,
            bookings: 12,
          },
          {
            room: isGerman
              ? "Team-Kooperationszentrum →"
              : "Team Collaboration Hub →",
            avgDuration: 2.1,
            efficiency: 68,
            bookings: 38,
          },
          {
            room: isGerman ? "Schulungszentrum" : "Training Center",
            avgDuration: 6.2,
            efficiency: 90,
            bookings: 8,
          },
        ],
        weeklyTrends: {
          shortBookings: [12, 18, 15, 20, 14],
          longBookings: [8, 12, 15, 10, 6],
          avgDuration: [2.1, 2.8, 3.2, 2.4, 2.0],
        },
      },

      // KPI 5: CO₂ Awareness Index
      co2Awareness: {
        overallScore: 65,
        changePercent: null,
        energyEfficiency: 60,
        wasteDiversion: 40,
        waterConservation: 75,
        carbonScore: {
          currentScore: 15,
          positiveActions: 5,
          warningZone: 10,
          benchmark: 70,
        },
        monthlyEmissions: [
          150, 200, 180, 220, 250, 280, 300, 290, 270, 240, 220, 200,
        ],
        monthlyEnergy: [80, 90, 85, 95, 105, 115, 120, 118, 112, 100, 95, 88],
      },
    },

    // === FEBRUARY 2024 ===
    {
      month: "2024-02",
      displayMonth: formatMonthDisplay(new Date(2024, 1, 29)),
      date: "2024-02-29",

      bookingUtilization: {
        rate: 71,
        changePercent: 4.41,
        bookedSlots: 568,
        availableSlots: 800,
        peakDays: isGerman
          ? ["Dienstag", "Donnerstag"]
          : ["Tuesday", "Thursday"],
        topSpaces: [
          {
            name: isGerman ? "Innovationslabor →" : "Innovation Lab →",
            utilization: 85,
          },
          {
            name: isGerman
              ? "Vorstandssitzungssaal →"
              : "Executive Boardroom →",
            utilization: 78,
          },
        ],
        hourlyUtilization: [
          { hour: "08:00", rate: 25 },
          { hour: "09:00", rate: 48 },
          { hour: "10:00", rate: 72 },
          { hour: "11:00", rate: 85 },
          { hour: "12:00", rate: 68 },
          { hour: "13:00", rate: 62 },
          { hour: "14:00", rate: 78 },
          { hour: "15:00", rate: 90 },
          { hour: "16:00", rate: 75 },
          { hour: "17:00", rate: 58 },
          { hour: "18:00", rate: 38 },
          { hour: "19:00", rate: 20 },
        ],
      },

      energyPerBooking: {
        avgEnergy: 13.8,
        changePercent: -2.82,
        totalEnergy: 7838.4,
        totalCost: 2194.75,
        peakHours: ["08:00-11:00", "14:00-17:00"],
        topConsumers: isGerman
          ? ["Heizung", "Beleuchtung"]
          : ["Heating", "Lighting"],
        spaceBreakdown: {
          meetingRooms: 41,
          conferenceRooms: 29,
          openOffice: 20,
          breakoutAreas: 10,
        },
        floorEnergyDistribution: [
          {
            floor: isGerman ? "Besprechungsräume" : "Meeting Rooms",
            percentage: 40.15,
            change: 2.66,
            emissions: 3147,
            color: "#2D5BFF",
          },
          {
            floor: isGerman ? "Konferenzräume" : "Conference Rooms",
            percentage: 27.5,
            change: -1.86,
            emissions: 2155,
            color: "#388BFF",
          },
          {
            floor: isGerman ? "Großraumbüro" : "Open Office",
            percentage: 22.8,
            change: -1.43,
            emissions: 1787,
            color: "#93AAFD",
          },
          {
            floor: isGerman ? "Pausenbereiche" : "Breakout Areas",
            percentage: 9.55,
            change: -1.95,
            emissions: 748,
            color: "#C6D2FD",
          },
        ],
        benchmarkComparison: 90,
      },

      comfortSatisfaction: {
        overallScore: 80,
        changePercent: 2.56,
        energyAnalytics: 70,
        dynamicOccupancy: 74,
        utilizationAnalytics: 77,
        monthlyTrend: {
          energyUse: 1280,
          co2Emissions: 384,
        },
      },

      bookingDuration: {
        avgDuration: 2.4,
        changePercent: 4.35,
        totalBookings: 256,
        efficiency: 84,
        peakBookingDays: isGerman
          ? ["Dienstag", "Mittwoch", "Donnerstag"]
          : ["Tuesday", "Wednesday", "Thursday"],
        peakBookingHours: ["09:00-11:00", "14:00-16:00"],
        durationDistribution: {
          lessThan1Hr: 42,
          oneToTwoHrs: 92,
          twoToFourHrs: 70,
          fourToSixHrs: 35,
          moreThanSixHrs: 17,
        },
        roomEfficiency: [
          {
            room: isGerman ? "Vorstandssitzungssaal" : "Executive Boardroom",
            avgDuration: 3.3,
            efficiency: 87,
            bookings: 30,
          },
          {
            room: isGerman ? "Innovationslabor →" : "Innovation Lab →",
            avgDuration: 1.9,
            efficiency: 74,
            bookings: 48,
          },
          {
            room: isGerman
              ? "Kundenbesprechungssuite →"
              : "Client Meeting Suite →",
            avgDuration: 4.6,
            efficiency: 96,
            bookings: 13,
          },
          {
            room: isGerman
              ? "Team-Kooperationszentrum →"
              : "Team Collaboration Hub →",
            avgDuration: 2.2,
            efficiency: 70,
            bookings: 40,
          },
          {
            room: isGerman ? "Schulungszentrum →" : "Training Center →",
            avgDuration: 6.3,
            efficiency: 91,
            bookings: 9,
          },
        ],
        weeklyTrends: {
          shortBookings: [13, 19, 16, 21, 15],
          longBookings: [9, 13, 16, 11, 7],
          avgDuration: [2.2, 2.9, 3.3, 2.5, 2.1],
        },
      },

      co2Awareness: {
        overallScore: 67,
        changePercent: 3.08,
        energyEfficiency: 62,
        wasteDiversion: 42,
        waterConservation: 77,
        carbonScore: {
          currentScore: 16,
          positiveActions: 6,
          warningZone: 10,
          benchmark: 70,
        },
        monthlyEmissions: [
          155, 205, 185, 225, 255, 285, 305, 295, 275, 245, 225, 205,
        ],
        monthlyEnergy: [82, 92, 87, 97, 107, 117, 122, 120, 114, 102, 97, 90],
      },
    },

    // Additional months would follow same pattern...
  ],
};

/**
 * Generate smart month data based on previous month
 */
function generateSmartMonthData(previousMonth, targetDate) {
  const month = targetDate.getMonth();
  const year = targetDate.getFullYear();

  // Calculate trend factors
  const baseTrend = 1 + (Math.random() * 0.04 - 0.02); // ±2% random variation
  const seasonalFactor = getSeasonalFactor(month);

  // Determine if it's vacation period (July/August)
  const isVacationPeriod = month === 6 || month === 7;
  const vacationFactor = isVacationPeriod ? 0.85 : 1.0;

  const monthString = `${year}-${String(month + 1).padStart(2, "0")}`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${lastDay}`;
  const displayMonth = formatMonthDisplay(targetDate);

  // Calculate new values with trends
  const prevUtil = previousMonth.bookingUtilization;
  const prevEnergy = previousMonth.energyPerBooking;
  const prevComfort = previousMonth.comfortSatisfaction;
  const prevDuration = previousMonth.bookingDuration;
  const prevCO2 = previousMonth.co2Awareness;

  // Booking utilization
  const newUtilRate = Math.round(prevUtil.rate * baseTrend * vacationFactor);
  const utilChange = (
    ((newUtilRate - prevUtil.rate) / prevUtil.rate) *
    100
  ).toFixed(2);

  // Energy per booking (affected by season)
  const newAvgEnergy = parseFloat(
    (prevEnergy.avgEnergy * seasonalFactor * baseTrend).toFixed(1)
  );
  const energyChange = (
    ((newAvgEnergy - prevEnergy.avgEnergy) / prevEnergy.avgEnergy) *
    100
  ).toFixed(2);

  // Comfort satisfaction
  const newComfortScore = Math.round(
    prevComfort.overallScore * baseTrend * (isVacationPeriod ? 0.92 : 1.0)
  );
  const comfortChange = (
    ((newComfortScore - prevComfort.overallScore) / prevComfort.overallScore) *
    100
  ).toFixed(2);

  // Booking duration
  const newDuration = parseFloat(
    (prevDuration.avgDuration * baseTrend * vacationFactor).toFixed(1)
  );
  const durationChange = (
    ((newDuration - prevDuration.avgDuration) / prevDuration.avgDuration) *
    100
  ).toFixed(2);

  // CO2 awareness
  const newCO2Score = Math.round(prevCO2.overallScore * baseTrend);
  const co2Change = (
    ((newCO2Score - prevCO2.overallScore) / prevCO2.overallScore) *
    100
  ).toFixed(2);

  return {
    month: monthString,
    displayMonth: displayMonth,
    date: dateString,

    bookingUtilization: {
      ...prevUtil,
      rate: newUtilRate,
      changePercent: parseFloat(utilChange),
      bookedSlots: Math.round((800 * newUtilRate) / 100),
    },

    energyPerBooking: {
      ...prevEnergy,
      avgEnergy: newAvgEnergy,
      changePercent: parseFloat(energyChange),
      totalEnergy: parseFloat(
        ((newAvgEnergy * ((800 * newUtilRate) / 100)) / 100).toFixed(1)
      ),
      totalCost: parseFloat(
        (((newAvgEnergy * ((800 * newUtilRate) / 100)) / 100) * 0.28).toFixed(2)
      ),
      benchmarkComparison: Math.round(95 + (Math.random() * 10 - 5)),
    },

    comfortSatisfaction: {
      ...prevComfort,
      overallScore: newComfortScore,
      changePercent: parseFloat(comfortChange),
      energyAnalytics: Math.round(newComfortScore * 0.92),
      dynamicOccupancy: Math.round(newComfortScore * 0.94),
      utilizationAnalytics: Math.round(newComfortScore * 0.96),
    },

    bookingDuration: {
      ...prevDuration,
      avgDuration: newDuration,
      changePercent: parseFloat(durationChange),
      totalBookings: Math.round(((800 * newUtilRate) / 100) * 0.45),
      efficiency: Math.round(85 + (Math.random() * 10 - 5)),
    },

    co2Awareness: {
      ...prevCO2,
      overallScore: newCO2Score,
      changePercent: parseFloat(co2Change),
      energyEfficiency: Math.round(newCO2Score * 0.93),
      wasteDiversion: Math.round(newCO2Score * 0.68),
      waterConservation: Math.round(newCO2Score * 1.08),
    },
  };
}

/**
 * Interpolate partial month data based on current day
 */
function interpolatePartialMonth(lastMonthData, currentDate) {
  const lastDataDate = new Date(lastMonthData.date);

  // Check if we're in the same month
  if (
    lastDataDate.getMonth() !== currentDate.getMonth() ||
    lastDataDate.getFullYear() !== currentDate.getFullYear()
  ) {
    return lastMonthData;
  }

  const daysInCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const currentDay = currentDate.getDate();
  const progressPercent = currentDay / daysInCurrentMonth;

  // Pro-rate metrics based on progress through month
  const interpolatedData = {
    ...lastMonthData,
    date: currentDate.toISOString().split("T")[0],
    displayMonth: formatMonthDisplay(currentDate),
    daysInMonth: currentDay,
    isPartial: true,

    bookingUtilization: {
      ...lastMonthData.bookingUtilization,
      bookedSlots: Math.round(
        lastMonthData.bookingUtilization.bookedSlots * progressPercent
      ),
    },

    energyPerBooking: {
      ...lastMonthData.energyPerBooking,
      totalEnergy: parseFloat(
        (lastMonthData.energyPerBooking.totalEnergy * progressPercent).toFixed(
          1
        )
      ),
      totalCost: parseFloat(
        (lastMonthData.energyPerBooking.totalCost * progressPercent).toFixed(2)
      ),
    },

    comfortSatisfaction: {
      ...lastMonthData.comfortSatisfaction,
      monthlyTrend: {
        energyUse: Math.round(
          lastMonthData.comfortSatisfaction.monthlyTrend.energyUse *
            progressPercent
        ),
        co2Emissions: Math.round(
          lastMonthData.comfortSatisfaction.monthlyTrend.co2Emissions *
            progressPercent
        ),
      },
    },

    bookingDuration: {
      ...lastMonthData.bookingDuration,
      totalBookings: Math.round(
        lastMonthData.bookingDuration.totalBookings * progressPercent
      ),
    },

    co2Awareness: {
      ...lastMonthData.co2Awareness,
      monthlyEmissions: lastMonthData.co2Awareness.monthlyEmissions.map(
        (emission) => Math.round(emission * progressPercent)
      ),
    },
  };

  return interpolatedData;
}

// Export function with smart auto-generation
export function getRentersTenantsDashboardData() {
  const today = new Date();
  let data = [...rentersTenantsDashboardData.monthlyData];

  // Step 1: Auto-generate missing months from last data point to today
  const lastDataDate = new Date(data[data.length - 1].date);
  let currentGenDate = new Date(lastDataDate);
  currentGenDate.setMonth(currentGenDate.getMonth() + 1);
  currentGenDate.setDate(1); // Start of next month

  // Generate all missing complete months
  while (currentGenDate < today) {
    const nextMonthStart = new Date(currentGenDate);
    nextMonthStart.setMonth(nextMonthStart.getMonth() + 1);

    // If next month starts after today, we're in the current month
    if (nextMonthStart > today) {
      break;
    }

    // Generate complete month data
    const lastGeneratedMonth = data[data.length - 1];
    const newMonthData = generateSmartMonthData(
      lastGeneratedMonth,
      currentGenDate
    );
    data.push(newMonthData);

    // Move to next month
    currentGenDate.setMonth(currentGenDate.getMonth() + 1);
  }

  // Step 2: Interpolate current partial month if needed
  const lastMonth = data[data.length - 1];
  const lastMonthDate = new Date(lastMonth.date);

  if (
    lastMonthDate.getMonth() === today.getMonth() &&
    lastMonthDate.getFullYear() === today.getFullYear()
  ) {
    // Same month - interpolate
    data[data.length - 1] = interpolatePartialMonth(lastMonth, today);
  } else if (lastMonthDate < today) {
    // Need to generate current month and interpolate
    const currentMonthData = generateSmartMonthData(lastMonth, today);
    const interpolatedCurrentMonth = interpolatePartialMonth(
      currentMonthData,
      today
    );
    data.push(interpolatedCurrentMonth);
  }

  return {
    ...rentersTenantsDashboardData,
    monthlyData: data,
    getCurrentMonthData() {
      return data[data.length - 1];
    },
    getDataByMonth(monthString) {
      return data.find((m) => m.month === monthString);
    },
    getDateRange() {
      return {
        start: data[0].date,
        end: data[data.length - 1].date,
      };
    },
  };
}
